/* eslint-disable react-refresh/only-export-components */
/* eslint-disable react/prop-types */
import { createContext, useState, useEffect, useContext, useMemo } from "react";
import UseClinicsData from "../Hooks/UseClinicsData";
import admin from "../Controllers/admin";

const ClinicContext = createContext();

const ALL_CLINIC_OPTION = {
  id: "all",
  title: "All",
};

export const ClinicProvider = ({ children }) => {
  const { clinicsData } = UseClinicsData();

  const [selectedClinic, setSelectedClinicState] = useState(() => {
    const storedClinic = localStorage.getItem("selectedClinic");
    return storedClinic ? JSON.parse(storedClinic) : undefined;
  });

  const roleName = String(admin?.role?.name || "").toLowerCase();
  const isDoctor = roleName === "doctor";
  const isClinic = roleName === "clinic";
  const isSuperAdmin = roleName === "super admin";

  const allowedClinics = useMemo(() => {
    const allClinics = Array.isArray(clinicsData) ? clinicsData : [];

    if (isSuperAdmin) {
      // When there's only one clinic in the deployment skip the ALL option.
      if (allClinics.length <= 1) return allClinics;
      return [ALL_CLINIC_OPTION, ...allClinics];
    }

    if (isDoctor) {
      const mapped = (admin?.doctor_clinics || []).map((item) => {
        const existing = allClinics.find(
          (clinic) => Number(clinic.id) === Number(item.clinic_id)
        );

        return (
          existing || {
            id: Number(item.clinic_id),
            title: item.clinic_title,
            clinic_id: Number(item.clinic_id),
            is_default: item.is_default,
            is_active: item.is_active,
          }
        );
      });

      const unique = mapped.filter(
        (item, index, arr) =>
          arr.findIndex((x) => String(x.id) === String(item.id)) === index
      );

      // If the doctor only belongs to one clinic, the ALL option adds noise.
      if (unique.length <= 1) return unique;
      return [ALL_CLINIC_OPTION, ...unique];
    }

    if (isClinic || admin?.clinic_id || admin?.owner_clinic?.clinic_id) {
      const ownerClinicId = admin?.owner_clinic?.clinic_id ?? admin?.clinic_id;
      const ownerClinic =
        allClinics.find((clinic) => Number(clinic.id) === Number(ownerClinicId)) ||
        {
          id: Number(ownerClinicId),
          title:
            admin?.owner_clinic?.clinic_title || admin?.clinic_title || "Clinic",
        };

      return ownerClinic ? [ownerClinic] : [];
    }

    return allClinics;
  }, [clinicsData, isDoctor, isClinic, isSuperAdmin]);

  const setSelectedClinic = (clinic) => {
    if (!clinic || String(clinic.id) === "all") {
      setSelectedClinicState(ALL_CLINIC_OPTION);
      localStorage.setItem("selectedClinic", JSON.stringify(ALL_CLINIC_OPTION));
      return;
    }

    setSelectedClinicState(clinic);
    localStorage.setItem("selectedClinic", JSON.stringify(clinic));
  };

  useEffect(() => {
    if (!admin) return;

    if (isClinic) {
      const ownerClinicId = admin?.owner_clinic?.clinic_id ?? admin?.clinic_id;
      const clinic =
        allowedClinics.find((item) => Number(item.id) === Number(ownerClinicId)) ||
        null;

      if (clinic) {
        setSelectedClinicState(clinic);
        localStorage.setItem("selectedClinic", JSON.stringify(clinic));
      }
      return;
    }

    if (isDoctor) {
      // Si el doctor solo pertenece a 1 clínica, forzar esa selección — el
      // dropdown ya oculta el "All" en ese caso (allowedClinics excluye ALL
      // cuando length === 1) y dejar selectedClinic=ALL confunde al user.
      if (allowedClinics.length === 1) {
        const only = allowedClinics[0];
        setSelectedClinicState(only);
        localStorage.setItem("selectedClinic", JSON.stringify(only));
        return;
      }

      const storedClinic = localStorage.getItem("selectedClinic");
      if (storedClinic) {
        const parsed = JSON.parse(storedClinic);
        const exists = allowedClinics.find(
          (item) => String(item.id) === String(parsed?.id)
        );
        if (exists) {
          setSelectedClinicState(exists);
          return;
        }
      }

      setSelectedClinicState(ALL_CLINIC_OPTION);
      localStorage.setItem("selectedClinic", JSON.stringify(ALL_CLINIC_OPTION));
      return;
    }

    if (isSuperAdmin) {
      // Espejo de la regla "1 sola clínica → directa" para Super Admin
      // cuando el deployment tiene una sola clínica.
      if (allowedClinics.length === 1) {
        const only = allowedClinics[0];
        setSelectedClinicState(only);
        localStorage.setItem("selectedClinic", JSON.stringify(only));
        return;
      }
      if (!selectedClinic) {
        setSelectedClinicState(ALL_CLINIC_OPTION);
        localStorage.setItem("selectedClinic", JSON.stringify(ALL_CLINIC_OPTION));
      }
      return;
    }

    if (admin?.clinic_id) {
      const clinic =
        allowedClinics.find(
          (item) => Number(item.id) === Number(admin.clinic_id)
        ) || null;

      if (clinic) {
        setSelectedClinicState(clinic);
        localStorage.setItem("selectedClinic", JSON.stringify(clinic));
      }
    }
    // selectedClinic se lee en el branch superadmin (línea ~143) para evitar
    // pisar una selección ya hecha por el user. NO va en deps a propósito:
    // los branches isClinic/isDoctor llaman setSelectedClinicState sin
    // condicionar a selectedClinic, así que agregarlo causaría loop infinito.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedClinics, isClinic, isDoctor, isSuperAdmin]);

  return (
    <ClinicContext.Provider
      value={{
        selectedClinic,
        setSelectedClinic,
        allowedClinics,
        allClinicOption: ALL_CLINIC_OPTION,
      }}
    >
      {children}
    </ClinicContext.Provider>
  );
};

export const useSelectedClinic = () => {
  return useContext(ClinicContext);
};